import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import specialtyService from '../services/specialtyService';
import { doctorService } from '../services/doctorService';
import florLogo from '../assets/florDaisiesSF.png';

const TODAS_SPECIALTY_IDS = [16, 15, 17, 18, 28];
const TODAS_TAB = { id: 'todas', name: 'Todas', icon: '🌐' };
const CACHE_KEY = 'daisies_lt_schedule_cache';
const BOOKING_URL = 'https://agenda.daisies.mx/horarios';

const saveToCache = (data, selectedId) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      availabilityByDate: data,
      selectedSpecialtyId: selectedId,
      timestamp: Date.now(),
    }));
  } catch (_) {}
};

const loadFromCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > 5 * 60 * 1000) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
};

const ScheduleSection = () => {
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState(TODAS_TAB);
  const [specialtyConfigs, setSpecialtyConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [consultationType, setConsultationType] = useState('all');
  const [pendingCount, setPendingCount] = useState(0);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const cancelledRef = useRef(false);
  const dropdownRef = useRef(null);
  const restoredFromCacheRef = useRef(false);
  const userChangedSpecialtyRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSpecialtyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const specs = await specialtyService.getFormattedOnDemandSpecialties();
        setSpecialties(specs);
        try {
          const configs = await specialtyService.getSpecialtyConfig();
          setSpecialtyConfigs(configs || {});
        } catch (_) {}

        const cached = loadFromCache();
        if (cached && cached.availabilityByDate && Object.keys(cached.availabilityByDate).length > 0) {
          setAvailabilityByDate(cached.availabilityByDate);
          restoredFromCacheRef.current = true;
          if (cached.selectedSpecialtyId === 'todas') {
            setSelectedSpecialty(TODAS_TAB);
          } else {
            const found = specs.find(s => s.id === cached.selectedSpecialtyId);
            if (found) setSelectedSpecialty(found);
          }
        }
      } catch (err) {
        console.warn('[ScheduleSection] Error loading specialties:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const isTodas = selectedSpecialty?.id === 'todas';
  const currentConfig = (!isTodas && selectedSpecialty) ? specialtyConfigs[selectedSpecialty.id] : null;

  const activeDurationId = useMemo(() => {
    if (isTodas) return null;
    if (!currentConfig) {
      return selectedSpecialty?.durationId || selectedSpecialty?.extraDurationId || 109;
    }
    if (consultationType === 'extra') {
      return currentConfig.extraDuration || currentConfig.duration || 109;
    }
    return currentConfig.duration || 109;
  }, [currentConfig, consultationType, selectedSpecialty, isTodas]);

  const mergeAvailability = useCallback((newSlots) => {
    setAvailabilityByDate(prev => {
      const updated = { ...prev };
      Object.entries(newSlots).forEach(([dateKey, slots]) => {
        if (!updated[dateKey]) updated[dateKey] = [];
        const existing = new Set(updated[dateKey].map(s => `${s.id}-${s.init}-${s.specialtyId || ''}`));
        slots.forEach(slot => {
          const key = `${slot.id}-${slot.init}-${slot.specialtyId || ''}`;
          if (!existing.has(key)) {
            updated[dateKey].push(slot);
            existing.add(key);
          }
        });
        updated[dateKey].sort((a, b) => {
          const tA = a.init.split(':').map(Number);
          const tB = b.init.split(':').map(Number);
          return (tA[0] * 60 + (tA[1] || 0)) - (tB[0] * 60 + (tB[1] || 0));
        });
      });
      return updated;
    });
  }, []);

  const processApiResponse = useCallback((data, date, isExtra, specialtyMeta, allowedDates) => {
    const organized = {};
    if (!data) return organized;
    const dayArray = data.result || data;
    if (!Array.isArray(dayArray)) return organized;
    const allowedSet = allowedDates ? new Set(allowedDates) : null;
    dayArray.forEach(dayData => {
      const dateKey = dayData.date || date;
      if (allowedSet && !allowedSet.has(dateKey)) return;
      if (!organized[dateKey]) organized[dateKey] = [];
      const slots = dayData.schedule_available || [];
      slots.forEach(slot => {
        organized[dateKey].push({
          id: slot.id,
          init: slot.init,
          end: slot.end,
          professional_ids: slot.professional_ids || [],
          date: dateKey,
          week_day: dayData.week_day,
          isExtra: isExtra,
          duration_id: slot.duration_id,
          ...(specialtyMeta ? {
            specialtyId: specialtyMeta.id,
            specialtyName: specialtyMeta.name,
            specialtyIcon: specialtyMeta.icon,
          } : {}),
        });
      });
    });
    return organized;
  }, []);

  // Load availability
  useEffect(() => {
    if (restoredFromCacheRef.current && !userChangedSpecialtyRef.current) {
      restoredFromCacheRef.current = false;
      return;
    }
    userChangedSpecialtyRef.current = false;

    cancelledRef.current = true;
    cancelledRef.current = false;
    const localCancelled = cancelledRef;

    if (isTodas) {
      setSlotsLoading(true);
      setAvailabilityByDate({});

      const today = new Date();
      const dates = [];
      for (let i = 0; i < 4; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        dates.push(d.toISOString().split('T')[0]);
      }

      const specsToFetch = TODAS_SPECIALTY_IDS.map(id => {
        const spec = specialties.find(s => s.id === id);
        const config = specialtyConfigs[id];
        if (!spec && !config) return null;
        const durationId = config?.duration || spec?.durationId || 109;
        return {
          id,
          durationId,
          name: config?.name || spec?.name || '',
          icon: config?.icon || spec?.icon || '💫',
        };
      }).filter(Boolean);

      if (specsToFetch.length === 0) {
        setSlotsLoading(false);
        return;
      }

      let remaining = specsToFetch.length * dates.length;
      setPendingCount(remaining);
      const allNewSlots = {};

      specsToFetch.forEach(specMeta => {
        dates.forEach(date => {
          doctorService.getAvailabilityByDatePublic(
            specMeta.id, specMeta.durationId, date, false, 'America/Mexico_City'
          )
            .then(res => {
              if (localCancelled.current) return;
              const organized = processApiResponse(res, date, false, specMeta, dates);
              mergeAvailability(organized);
              Object.entries(organized).forEach(([dk, sl]) => {
                if (!allNewSlots[dk]) allNewSlots[dk] = [];
                allNewSlots[dk].push(...sl);
              });
            })
            .catch(() => {})
            .finally(() => {
              if (localCancelled.current) return;
              remaining--;
              setPendingCount(remaining);
              if (remaining <= 0) {
                setSlotsLoading(false);
                setAvailabilityByDate(prev => {
                  saveToCache(prev, 'todas');
                  return prev;
                });
              }
            });
        });
      });

      return () => { localCancelled.current = true; };
    }

    // Single specialty
    if (!selectedSpecialty) return;

    const loadAvailability = async () => {
      setSlotsLoading(true);
      setAvailabilityByDate({});

      try {
        const today = new Date();
        const dates = [];
        for (let i = 0; i < 4; i++) {
          const d = new Date(today);
          d.setDate(today.getDate() + i);
          dates.push(d.toISOString().split('T')[0]);
        }

        const durationIds = [];
        if (consultationType === 'all' && currentConfig) {
          if (currentConfig.duration) durationIds.push({ id: currentConfig.duration, isExtra: false });
          if (currentConfig.extraDuration && currentConfig.extraDuration !== currentConfig.duration) {
            durationIds.push({ id: currentConfig.extraDuration, isExtra: true });
          }
        }
        if (durationIds.length === 0) {
          durationIds.push({ id: activeDurationId, isExtra: consultationType === 'extra' });
        }

        const fetches = [];
        for (const date of dates) {
          for (const dur of durationIds) {
            fetches.push(
              doctorService.getAvailabilityByDatePublic(
                selectedSpecialty.id, dur.id, date, false, 'America/Mexico_City'
              ).then(res => ({ date, data: res, isExtra: dur.isExtra }))
               .catch(() => ({ date, data: null, isExtra: dur.isExtra }))
            );
          }
        }

        const results = await Promise.all(fetches);
        if (localCancelled.current) return;

        const organized = {};
        results.forEach(({ date, data, isExtra }) => {
          const partial = processApiResponse(data, date, isExtra, null, dates);
          Object.entries(partial).forEach(([dateKey, slots]) => {
            if (!organized[dateKey]) organized[dateKey] = [];
            organized[dateKey].push(...slots);
          });
        });

        Object.keys(organized).forEach(dateKey => {
          const seen = new Set();
          organized[dateKey] = organized[dateKey].filter(slot => {
            const key = `${slot.id}-${slot.init}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          organized[dateKey].sort((a, b) => {
            const tA = a.init.split(':').map(Number);
            const tB = b.init.split(':').map(Number);
            return (tA[0] * 60 + (tA[1] || 0)) - (tB[0] * 60 + (tB[1] || 0));
          });
        });

        setAvailabilityByDate(organized);
        saveToCache(organized, selectedSpecialty.id);
      } catch (err) {
        console.warn('[ScheduleSection] Error loading availability:', err);
      } finally {
        if (!localCancelled.current) setSlotsLoading(false);
      }
    };

    loadAvailability();
    return () => { localCancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecialty, consultationType, activeDurationId, currentConfig, specialties, specialtyConfigs, isTodas]);

  const formatTime = (time) => {
    const [h, m] = time.split(':');
    const hr = parseInt(h);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    return `${hr % 12 || 12}:${m} ${ampm}`;
  };

  const formatDayLabel = (dateStr) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    if (dateStr === todayStr) return 'Hoy';
    if (dateStr === tomorrowStr) return 'Mañana';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const groupByTimeOfDay = (slots) => {
    const groups = { morning: [], afternoon: [], evening: [] };
    slots.forEach(slot => {
      const hour = parseInt(slot.init.split(':')[0]);
      if (hour >= 7 && hour < 12) groups.morning.push(slot);
      else if (hour >= 12 && hour < 18) groups.afternoon.push(slot);
      else groups.evening.push(slot);
    });
    return groups;
  };

  const abbreviateName = (name) => {
    if (!name) return '';
    const map = {
      'Psicología': 'Psico',
      'Ginecología': 'Gineco',
      'Nutrición': 'Nutri',
      'Salud General y Skincare': 'Salud',
      'Entrenamiento': 'Entreno',
    };
    return map[name] || name.slice(0, 6);
  };

  const sortedDates = Object.keys(availabilityByDate).sort();
  const totalSlots = sortedDates.reduce((sum, d) => sum + availabilityByDate[d].length, 0);
  const currentSpecialtyDisplay = isTodas ? TODAS_TAB : specialties.find(s => s.id === selectedSpecialty?.id) || selectedSpecialty;
  const hasAnySlots = sortedDates.length > 0;
  const isFullyLoaded = !slotsLoading;
  const showGrid = hasAnySlots || slotsLoading;

  const handleSlotClick = (slot) => {
    window.open(BOOKING_URL, '_blank', 'noopener,noreferrer');
  };

  const renderSlotButton = (slot, i, colorClasses) => (
    <button
      key={`${slot.id}-${slot.init}-${slot.specialtyId || ''}-${i}`}
      onClick={() => handleSlotClick(slot)}
      className={`press-scale font-medium rounded-lg transition-all text-center ${colorClasses} ${
        isTodas
          ? 'text-[10px] sm:text-xs px-1.5 py-1.5 sm:px-2 sm:py-2'
          : 'text-xs px-2 py-2'
      }`}
    >
      <span className="block">{formatTime(slot.init)}</span>
      {isTodas && slot.specialtyIcon && (
        <span className="block text-[9px] sm:text-[10px] opacity-70 mt-0.5 truncate leading-tight">
          {slot.specialtyIcon} {abbreviateName(slot.specialtyName)}
        </span>
      )}
    </button>
  );

  const renderTimeGroup = (label, emoji, slots, colorClasses) => {
    if (slots.length === 0) return null;
    return (
      <div>
        <p className="text-[10px] sm:text-xs font-semibold text-text-secondary mb-1 sm:mb-2 flex items-center gap-1">
          {emoji} {label}
        </p>
        <div className={`hidden md:grid gap-1 sm:gap-1.5 ${isTodas ? 'grid-cols-2 sm:grid-cols-2' : 'grid-cols-3 sm:grid-cols-2'}`}>
          {slots.map((slot, i) => renderSlotButton(slot, i, colorClasses))}
        </div>
        <div className="flex md:hidden flex-wrap gap-1.5">
          {slots.map((slot, i) => (
            <button
              key={`m-${slot.id}-${slot.init}-${slot.specialtyId || ''}-${i}`}
              onClick={() => handleSlotClick(slot)}
              className={`press-scale font-medium rounded-lg transition-all text-center text-xs px-3 py-2 ${colorClasses}`}
            >
              <span className="block">{formatTime(slot.init)}</span>
              {isTodas && slot.specialtyIcon && (
                <span className="block text-[9px] opacity-70 mt-0.5 truncate leading-tight">
                  {slot.specialtyIcon} {abbreviateName(slot.specialtyName)}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="py-12 flex flex-col items-center justify-center">
        <img src={florLogo} alt="Cargando" className="h-12 w-12 animate-spin" style={{ animationDuration: '2s' }} />
        <p className="mt-3 text-sm text-text-secondary font-medium">Cargando horarios...</p>
      </div>
    );
  }

  return (
    <div className="w-full px-3 sm:px-4 pb-8 sm:pb-12">
      {/* Section header */}
      <div className="text-center mb-4 sm:mb-6 pt-6 sm:pt-10">
        <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-text-primary tracking-tight mb-1 sm:mb-3">
          Todos los <span className="text-daisies">horarios</span> disponibles
        </h2>
        <p className="text-text-secondary text-xs sm:text-base md:text-lg max-w-2xl mx-auto">
          Explora los horarios abiertos por especialidad y agenda en un click
        </p>
      </div>

      {/* Specialty dropdown */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="relative flex-1 sm:flex-initial sm:min-w-[240px]" ref={dropdownRef}>
          <button
            onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)}
            className="w-full px-4 py-3 rounded-xl bg-white shadow-soft hover:shadow-elevated transition-all text-left flex items-center justify-between gap-2"
          >
            <div>
              <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold block">Especialidad</span>
              <span className="text-sm font-semibold text-text-primary">
                {currentSpecialtyDisplay?.icon} {currentSpecialtyDisplay?.name || 'Todas'}
              </span>
            </div>
            <ChevronDown size={16} className={`text-text-secondary transition-transform ${showSpecialtyDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showSpecialtyDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-elevated border border-black/5 z-50 max-h-64 overflow-y-auto">
              <button
                onClick={() => {
                  userChangedSpecialtyRef.current = true;
                  setSelectedSpecialty(TODAS_TAB);
                  setShowSpecialtyDropdown(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-daisies-50 transition-colors flex items-center gap-2 ${
                  isTodas ? 'bg-daisies-50 text-daisies font-semibold' : 'text-text-primary'
                }`}
              >
                <span>🌐</span>
                <span>Todas</span>
              </button>
              {specialties.map(spec => (
                <button
                  key={spec.id}
                  onClick={() => {
                    userChangedSpecialtyRef.current = true;
                    setSelectedSpecialty(spec);
                    setShowSpecialtyDropdown(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-daisies-50 transition-colors flex items-center gap-2 ${
                    selectedSpecialty?.id === spec.id ? 'bg-daisies-50 text-daisies font-semibold' : 'text-text-primary'
                  }`}
                >
                  <span>{spec.icon}</span>
                  <span>{spec.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Consultation type filter (single specialty only) */}
        {!isTodas && (
          <div className="inline-flex bg-white rounded-xl shadow-soft p-1 gap-1">
            {[
              { key: 'all', label: 'Todas', icon: '📋' },
              { key: 'included', label: 'Incluida', icon: '✅' },
              { key: 'extra', label: 'Extra', icon: '⭐' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setConsultationType(opt.key)}
                className={`press-scale px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center gap-1 sm:gap-1.5 ${
                  consultationType === opt.key
                    ? 'bg-daisies text-white shadow-soft'
                    : 'text-text-secondary hover:bg-surface'
                }`}
              >
                <span>{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Schedule grid / list */}
      {!isTodas && slotsLoading && sortedDates.length === 0 ? (
        <div className="py-10 flex flex-col items-center justify-center">
          <img src={florLogo} alt="Cargando" className="h-10 w-10 animate-spin" style={{ animationDuration: '2s' }} />
          <p className="mt-3 text-sm text-text-secondary font-medium">
            Cargando horarios de {selectedSpecialty?.name || 'la especialidad'}...
          </p>
        </div>
      ) : !showGrid && isFullyLoaded ? (
        <div className="text-center py-10 sm:py-16 bg-white rounded-card shadow-soft">
          <Calendar className="mx-auto mb-3 sm:mb-4 text-text-secondary" size={36} />
          <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-2">Sin horarios disponibles</h3>
          <p className="text-text-secondary text-xs sm:text-sm">
            No hay horarios disponibles para {isTodas ? 'estas especialidades' : (selectedSpecialty?.name || 'esta especialidad')} en los próximos días.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <h3 className="text-sm sm:text-lg font-bold text-text-primary">
              {isTodas ? (
                <>🌐 Todas las especialidades</>
              ) : (
                <>
                  {selectedSpecialty?.icon} {selectedSpecialty?.name}
                  {consultationType !== 'all' && (
                    <span className="text-xs sm:text-sm font-medium text-text-secondary ml-2">
                      — {consultationType === 'included' ? 'Consulta Incluida' : 'Consulta Extra'}
                    </span>
                  )}
                </>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {slotsLoading && pendingCount > 0 && (
                <span className="text-[10px] sm:text-xs text-daisies animate-pulse font-medium">
                  Cargando...
                </span>
              )}
              <span className="text-[10px] sm:text-sm text-text-secondary">{totalSlots} disponibles</span>
            </div>
          </div>

          {/* DESKTOP: grid layout */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sortedDates.map(dateKey => {
              const slots = availabilityByDate[dateKey];
              const groups = groupByTimeOfDay(slots);
              return (
                <div key={dateKey} className="bg-white rounded-card shadow-soft p-5">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-black/5">
                    <Calendar size={16} className="text-daisies" />
                    <h4 className="font-bold text-text-primary text-sm capitalize">
                      {formatDayLabel(dateKey)}
                    </h4>
                    <span className="ml-auto text-xs text-text-secondary bg-surface px-2 py-0.5 rounded-full">
                      {slots.length}
                    </span>
                  </div>
                  <div className="space-y-4">
                    {renderTimeGroup('Mañana', '🌅', groups.morning, 'bg-blue-50 text-blue-700 hover:bg-daisies hover:text-white')}
                    {renderTimeGroup('Tarde', '☀️', groups.afternoon, 'bg-orange-50 text-orange-700 hover:bg-daisies hover:text-white')}
                    {renderTimeGroup('Noche', '🌙', groups.evening, 'bg-daisies-50 text-daisies hover:bg-daisies hover:text-white')}
                    {slots.length === 0 && (
                      <p className="text-xs text-text-secondary text-center py-4">Sin horarios</p>
                    )}
                  </div>
                </div>
              );
            })}

            {isTodas && slotsLoading && sortedDates.length < 4 && (
              <>
                {sortedDates.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center py-6">
                    <img src={florLogo} alt="Cargando" className="h-10 w-10 animate-spin" style={{ animationDuration: '2s' }} />
                    <p className="mt-2 text-xs text-text-secondary">Cargando horarios...</p>
                  </div>
                )}
                {Array.from({ length: 4 - sortedDates.length }).map((_, i) => (
                  <div key={`placeholder-${i}`} className="bg-white rounded-card shadow-soft p-5 animate-pulse">
                    <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-black/5">
                      <div className="w-3 h-3 bg-gray-200 rounded" />
                      <div className="w-16 h-3 bg-gray-200 rounded" />
                    </div>
                    <div className="space-y-2">
                      <div className="w-12 h-2.5 bg-gray-100 rounded" />
                      <div className="grid grid-cols-2 gap-1">
                        {Array.from({ length: 4 }).map((_, j) => (
                          <div key={j} className="h-7 bg-gray-100 rounded-lg" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* MOBILE: list layout */}
          <div className="flex flex-col gap-3 md:hidden">
            {sortedDates.map(dateKey => {
              const slots = availabilityByDate[dateKey];
              const groups = groupByTimeOfDay(slots);
              return (
                <div key={dateKey} className="bg-white rounded-card shadow-soft p-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-black/5">
                    <Calendar size={14} className="text-daisies" />
                    <h4 className="font-bold text-text-primary text-base capitalize">
                      {formatDayLabel(dateKey)}
                    </h4>
                    <span className="ml-auto text-xs text-text-secondary bg-surface px-2 py-0.5 rounded-full">
                      {slots.length} horarios
                    </span>
                  </div>
                  <div className="space-y-3">
                    {renderTimeGroup('Mañana', '🌅', groups.morning, 'bg-blue-50 text-blue-700 hover:bg-daisies hover:text-white')}
                    {renderTimeGroup('Tarde', '☀️', groups.afternoon, 'bg-orange-50 text-orange-700 hover:bg-daisies hover:text-white')}
                    {renderTimeGroup('Noche', '🌙', groups.evening, 'bg-daisies-50 text-daisies hover:bg-daisies hover:text-white')}
                    {slots.length === 0 && (
                      <p className="text-xs text-text-secondary text-center py-3">Sin horarios</p>
                    )}
                  </div>
                </div>
              );
            })}

            {isTodas && slotsLoading && sortedDates.length < 4 && (
              <>
                {sortedDates.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6">
                    <img src={florLogo} alt="Cargando" className="h-10 w-10 animate-spin" style={{ animationDuration: '2s' }} />
                    <p className="mt-2 text-sm text-text-secondary">Cargando horarios...</p>
                  </div>
                )}
                {Array.from({ length: 4 - sortedDates.length }).map((_, i) => (
                  <div key={`m-placeholder-${i}`} className="bg-white rounded-card shadow-soft p-4 animate-pulse">
                    <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-black/5">
                      <div className="w-3 h-3 bg-gray-200 rounded" />
                      <div className="w-20 h-4 bg-gray-200 rounded" />
                    </div>
                    <div className="space-y-2">
                      <div className="w-12 h-2.5 bg-gray-100 rounded" />
                      <div className="flex flex-wrap gap-1.5">
                        {Array.from({ length: 6 }).map((_, j) => (
                          <div key={j} className="h-8 w-16 bg-gray-100 rounded-lg" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleSection;
